import React, { useState } from "react";
import {
  Button,
  Checkbox,
  Flex,
  Input,
  SegmentedControl,
  Text,
  toast,
} from "@appsmith/ads";

import SupportAccessApi, {
  SupportAccessScope,
  SupportAccessTokenResponse,
} from "api/SupportAccessApi";
import { LICENSE } from "constants/RouteContants";
import { PageContainer } from "global-styles/global.styles";
import { ApiResponse } from "types/ApiTypes";
import { withAuthentication, withEmailVerification } from "utils/RouterUtils";
import {
  endSupportAccessSession,
  getSupportAccessSession,
  setSupportAccessSession,
} from "utils/SupportAccessUtils";

type TargetField =
  | "customerEmail"
  | "customerId"
  | "licenseId"
  | "externalLicenseId";

// Segment labels stay short — the segmented control wraps longer labels
// mid-phrase; the full field description lives in the input placeholder.
const TARGET_OPTIONS: Array<{
  label: string;
  placeholder: string;
  value: TargetField;
}> = [
  {
    label: "Email",
    placeholder: "Customer email",
    value: "customerEmail",
  },
  {
    label: "Stripe ID",
    placeholder: "Stripe customer ID (cus_...)",
    value: "customerId",
  },
  {
    label: "License ID",
    placeholder: "License ID",
    value: "licenseId",
  },
  {
    label: "External ID",
    placeholder: "External (Keygen) license ID",
    value: "externalLicenseId",
  },
];

const SCOPE_OPTIONS: Array<{
  label: string;
  description: string;
  value: SupportAccessScope;
}> = [
  {
    label: "Manage users",
    description: "Invite, assign, unassign, and remove members.",
    value: "members:write",
  },
  {
    label: "Manage billing",
    description: "Update seat counts and start checkout.",
    value: "billing:write",
  },
  {
    label: "Open Stripe dashboard",
    description: "Create the customer dashboard link.",
    value: "stripe:dashboard",
  },
];

function SupportAccess() {
  const [targetField, setTargetField] = useState<TargetField>("customerEmail");
  const [targetValue, setTargetValue] = useState("");
  const [reason, setReason] = useState("");
  const [scopes, setScopes] = useState<Set<SupportAccessScope>>(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [activeSession, setActiveSession] = useState(getSupportAccessSession());

  const selectedTarget = TARGET_OPTIONS.find(
    (option) => option.value === targetField,
  );

  const handleTargetFieldChange = (nextTargetField: string) => {
    setTargetField(nextTargetField as TargetField);
    setTargetValue("");
  };

  const handleScopeChange = (scope: SupportAccessScope, checked: boolean) => {
    setScopes((currentScopes) => {
      const nextScopes = new Set(currentScopes);
      if (checked) {
        nextScopes.add(scope);
      } else {
        nextScopes.delete(scope);
      }
      return nextScopes;
    });
  };

  const handleCreateToken = async () => {
    const trimmedTarget = targetValue.trim();
    const trimmedReason = reason.trim();

    if (!trimmedTarget) {
      toast.show("Choose a customer identifier.", { kind: "error" });
      return;
    }

    if (!trimmedReason) {
      toast.show("Enter the support reason.", { kind: "error" });
      return;
    }

    if (
      activeSession &&
      !window.confirm(
        `Replace the active support session for ${activeSession.subjectEmail}?`,
      )
    ) {
      return;
    }

    setIsLoading(true);

    try {
      if (activeSession) {
        // Best-effort revoke of the abandoned session's delegated token
        // before replacing it. Must run before the local session is replaced,
        // since the revoke call authenticates with the delegated token.
        await endSupportAccessSession();
        setActiveSession(null);
      }

      const response = (await SupportAccessApi.createToken({
        [targetField]: trimmedTarget,
        reason: trimmedReason,
        scopes: Array.from(scopes),
      })) as unknown as ApiResponse<SupportAccessTokenResponse>;

      if (!response?.data?.token) {
        throw new Error("Support access token was not returned.");
      }

      setSupportAccessSession(response.data);
      toast.show(`Support access started for ${response.data.subjectEmail}.`, {
        kind: "success",
      });
      // Full reload prevents customer-scoped Redux state from carrying across support sessions.
      window.location.assign(LICENSE);
    } catch (error: any) {
      toast.show(getErrorMessage(error), { kind: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExit = async () => {
    // Best-effort revoke of the delegated token before clearing the session.
    await endSupportAccessSession();
    setActiveSession(null);
    // Full reload clears customer-scoped Redux state from the delegated
    // session. Must land on a whitelisted Auth0 callback route —
    // /support-access is not one, and reloading there hangs the auth loader.
    window.location.assign(LICENSE);
  };

  return (
    <PageContainer hasSidebar={false}>
      <Flex flexDirection="column" gap="spaces-7" maxWidth="760px">
        <Flex flexDirection="column" gap="spaces-2">
          <Text className="!font-semibold" kind="heading-l">
            Support customer access
          </Text>
          <Text kind="body-m">
            Start a time-limited customer session for support work.
          </Text>
        </Flex>

        {activeSession && (
          <Flex
            alignItems="center"
            border="1px solid var(--ads-v2-color-border)"
            borderRadius="8px"
            justifyContent="space-between"
            padding="spaces-5"
          >
            <Flex flexDirection="column" gap="spaces-1">
              <Text className="!font-medium" kind="body-m">
                Active session
              </Text>
              <Text kind="body-s">
                {activeSession.actorEmail} supporting{" "}
                {activeSession.subjectEmail}
              </Text>
            </Flex>
            <Button kind="secondary" onClick={handleExit} size="md">
              Exit support access
            </Button>
          </Flex>
        )}

        <Flex
          border="1px solid var(--ads-v2-color-border)"
          borderRadius="8px"
          flexDirection="column"
          gap="spaces-6"
          padding="spaces-7"
        >
          <Flex flexDirection="column" gap="spaces-3">
            <Text className="!font-semibold" kind="heading-s">
              Customer
            </Text>
            <SegmentedControl
              defaultValue={targetField}
              isFullWidth
              onChange={handleTargetFieldChange}
              options={TARGET_OPTIONS}
            />
            <Input
              onChange={(value: string) => setTargetValue(value)}
              placeholder={selectedTarget?.placeholder}
              size="md"
              value={targetValue}
            />
          </Flex>

          <Flex flexDirection="column" gap="spaces-2">
            <Text className="!font-semibold" kind="heading-s">
              Reason
            </Text>
            <Input
              onChange={(value: string) => setReason(value)}
              placeholder="Support ticket, incident, or customer-approved reason"
              renderAs="textarea"
              size="md"
              value={reason}
            />
          </Flex>

          <Flex flexDirection="column" gap="spaces-3">
            <Text className="!font-semibold" kind="heading-s">
              Extra permissions
            </Text>
            {SCOPE_OPTIONS.map((scope) => (
              <Flex alignItems="flex-start" gap="spaces-3" key={scope.value}>
                <Checkbox
                  isSelected={scopes.has(scope.value)}
                  onChange={(checked: boolean) =>
                    handleScopeChange(scope.value, checked)
                  }
                />
                <Flex flexDirection="column" gap="spaces-1">
                  <Text kind="body-m">{scope.label}</Text>
                  <Text color="var(--ads-v2-color-fg-muted)" kind="body-s">
                    {scope.description}
                  </Text>
                </Flex>
              </Flex>
            ))}
          </Flex>

          <Flex justifyContent="flex-end">
            <Button
              isLoading={isLoading}
              kind="primary"
              onClick={handleCreateToken}
              size="md"
            >
              Start support session
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </PageContainer>
  );
}

function getErrorMessage(error: any) {
  return (
    error?.responseMeta?.message ||
    error?.responseMeta?.error?.message ||
    error?.message ||
    "Unable to start support access."
  );
}

export default withAuthentication(withEmailVerification(SupportAccess));
